import { escapeHtml } from '../utils/html-utils.js';
import {
  getWidgetHostDomain,
  loadWidgetHostPreview,
  widgetHostUnavailableMessage
} from '../utils/widget-iframe-host.js';
import { extractWidgetFetchHosts } from '../utils/widget-fetch-hosts.js';
import {
  renderPreviewConfigForm,
  readPreviewConfigFromDom
} from '../utils/widget-preview-config.js';

const PREVIEW_DEBOUNCE_MS = 300;

class AdminWidgetManagement extends HTMLElement {
  constructor() {
    super();
    this.widgets = [];
    this.loading = true;
    this.reviewWidgetId = null;
    this.widgetDetailCache = {};
    this.reviewPreviewError = null;
    this.previewConfigValues = {};
    this._previewDebounceTimer = null;
  }

  disconnectedCallback() {
    if (this._previewDebounceTimer) {
      clearTimeout(this._previewDebounceTimer);
      this._previewDebounceTimer = null;
    }
  }

  connectedCallback() {
    this.render();
    this.loadWidgets();
  }

  async loadWidgets() {
    try {
      const res = await window.API.request('/widgets/admin/all');
      this.widgets = res.widgets || [];
      this.loading = false;
      this.render();
      this.attachListeners();
    } catch (err) {
      window.logger.error(err);
      this.loading = false;
      this.innerHTML = '<div class="admin-widget-management"><p class="help-error">Failed to load widgets.</p></div>';
    }
  }

  async updateStatus(id, status, rejectionReason) {
    try {
      await window.API.request(`/widgets/admin/${id}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status, rejectionReason: rejectionReason || null })
      });
      delete this.widgetDetailCache[id];
      if (this.reviewWidgetId === id) {
        this.reviewWidgetId = null;
        this.reviewPreviewError = null;
      }
      await this.loadWidgets();
    } catch (err) {
      alert(err.message || 'Update failed');
    }
  }

  updateReviewPreviewErrorDom(widgetId) {
    const el = this.querySelector(`[data-preview-error="review-${widgetId}"]`);
    if (el) {
      el.textContent = this.reviewPreviewError || '';
      el.hidden = !this.reviewPreviewError;
    }
  }

  capturePreviewConfigState() {
    if (!this.reviewWidgetId) return;
    const key = `review-${this.reviewWidgetId}`;
    this.previewConfigValues[key] = readPreviewConfigFromDom(this, key);
  }

  async updateReviewPreview(widgetId) {
    const iframe = this.querySelector(`.admin-widget-preview-frame[data-widget-id="${widgetId}"]`);
    if (!iframe) return;
    try {
      const res = await window.API.request(`/widgets/${widgetId}/preview-token`, { method: 'POST' });
      this.reviewPreviewError = null;
      const host = getWidgetHostDomain();
      loadWidgetHostPreview(
        iframe,
        `${host}/render/preview/${widgetId}?token=${encodeURIComponent(res.token)}`,
        {
          widgetId,
          getInstanceConfig: () => readPreviewConfigFromDom(this, `review-${widgetId}`),
          onError: () => {
            this.reviewPreviewError = widgetHostUnavailableMessage();
            this.updateReviewPreviewErrorDom(widgetId);
          }
        }
      );
      this.updateReviewPreviewErrorDom(widgetId);
    } catch (err) {
      window.logger.warn('Admin widget preview failed:', err);
      this.reviewPreviewError = widgetHostUnavailableMessage();
      this.updateReviewPreviewErrorDom(widgetId);
    }
  }

  async toggleReview(id) {
    if (this.reviewWidgetId === id) {
      this.capturePreviewConfigState();
      this.reviewWidgetId = null;
      this.reviewPreviewError = null;
      this.render();
      this.attachListeners();
      return;
    }

    this.reviewWidgetId = id;
    this.reviewPreviewError = null;

    if (!this.widgetDetailCache[id]) {
      try {
        const res = await window.API.request(`/widgets/${id}`);
        this.widgetDetailCache[id] = res.widget;
      } catch (err) {
        window.logger.error(err);
        alert('Could not load widget for review');
        this.reviewWidgetId = null;
        return;
      }
    }

    this.render();
    this.attachListeners();
    await this.updateReviewPreview(id);
  }

  renderModerationSection(w) {
    const sc = w.systemCatalogStatus || 'none';
    if (sc !== 'pending') {
      return '';
    }
    return `
      <div class="admin-widget-moderation">
        <div class="form-group">
          <label class="form-label" for="admin-rejection-${w.id}">Feedback for creator</label>
          <textarea
            id="admin-rejection-${w.id}"
            class="form-control"
            data-rejection-comment="${w.id}"
            rows="3"
            maxlength="2000"
            placeholder="Required when rejecting — explain what needs to change"
          ></textarea>
          <p class="form-text">The creator will see this message on My Widgets when the widget is rejected.</p>
        </div>
        <div class="admin-widget-moderation-actions">
          <button type="button" class="btn btn-primary btn-sm" data-action="approve" data-id="${w.id}">Approve</button>
          <button type="button" class="btn btn-secondary btn-sm" data-action="reject" data-id="${w.id}">Reject with feedback</button>
        </div>
      </div>
    `;
  }

  getRejectionComment(widgetId) {
    return this.querySelector(`[data-rejection-comment="${widgetId}"]`)?.value?.trim() || '';
  }

  async rejectWidget(id) {
    const reason = this.getRejectionComment(id);
    if (!reason) {
      const field = this.querySelector(`[data-rejection-comment="${id}"]`);
      field?.focus();
      field?.classList.add('is-invalid');
      alert('Please add feedback explaining why the widget was rejected.');
      return;
    }
    if (!window.confirm('Reject this widget and send feedback to the creator?')) {
      return;
    }
    await this.updateStatus(id, 'rejected', reason);
  }

  renderOAuthAudit(widget) {
    if (!widget?.oauthConfig) return '';
    const cfg = widget.oauthConfig;
    const scopes = (cfg.scopes || []).join(' ') || '(none)';
    const tokenHost = (() => {
      try { return new URL(cfg.tokenUrl || cfg.token_url).hostname; } catch { return '(invalid)'; }
    })();
    const authHost = (() => {
      try { return new URL(cfg.authorizationUrl || cfg.authorization_url).hostname; } catch { return '(invalid)'; }
    })();
    const allowed = widget.allowedHosts || widget.networkPolicy?.allowedHosts || [];
    const apiWarn = tokenHost && allowed.length && !allowed.some((h) => {
      if (h.startsWith('*.')) {
        const suffix = h.slice(1);
        return tokenHost.endsWith(suffix) || tokenHost === h.slice(2);
      }
      return h === tokenHost || h === authHost;
    })
      ? `<p class="form-text widget-hosts-warn" role="alert">OAuth token host (${escapeHtml(tokenHost)}) may not match allowed API hosts.</p>`
      : '';
    return `
      <div class="admin-widget-oauth-audit">
        <h4>OAuth configuration</h4>
        <p class="form-text"><strong>Service:</strong> ${escapeHtml(cfg.serviceLabel || 'service')}</p>
        <p class="form-text"><strong>Authorization URL:</strong> ${escapeHtml(cfg.authorizationUrl || cfg.authorization_url || '')}</p>
        <p class="form-text"><strong>Token URL:</strong> ${escapeHtml(cfg.tokenUrl || cfg.token_url || '')}</p>
        <p class="form-text"><strong>Scopes:</strong> ${escapeHtml(scopes)}</p>
        <p class="form-text"><strong>Client ID:</strong> ${escapeHtml(widget.oauthClientId || '(not set)')}</p>
        <p class="form-text"><strong>Client secret:</strong> ${widget.hasOAuthClientSecret ? 'configured' : '(not set)'}</p>
        ${apiWarn}
      </div>
    `;
  }

  renderHostsAudit(widget) {
    if (!widget) return '';
    const declared = (widget.allowedHosts || []).join(', ') || '(none)';
    const extracted = extractWidgetFetchHosts(widget.jsContent || '');
    const extractedText = extracted.length ? extracted.join(', ') : '(none found in JS)';
    const missing = extracted.filter((h) => !(widget.allowedHosts || []).some((allowed) => {
      if (allowed.startsWith('*.')) {
        const suffix = allowed.slice(1);
        return h.endsWith(suffix) || h === allowed.slice(2);
      }
      return h === allowed;
    }));
    const warn = missing.length
      ? `<p class="form-text widget-hosts-warn" role="alert">JS calls hosts not in allowed list: ${escapeHtml(missing.join(', '))}</p>`
      : '';
    return `
      <div class="admin-widget-hosts-audit">
        <h4>Allowed API hosts</h4>
        <p class="form-text"><strong>Declared:</strong> ${escapeHtml(declared)}</p>
        <p class="form-text"><strong>From JS scan:</strong> ${escapeHtml(extractedText)}</p>
        ${warn}
      </div>
    `;
  }

  renderCodeSection(widget) {
    if (!widget) {
      return '';
    }
    return `
      <div class="admin-widget-code">
        <h4>Source code</h4>
        <p class="form-text">Review HTML, CSS, and JavaScript before approving.</p>
        <div class="widget-code-grid">
          <div class="form-group">
            <label class="form-label">HTML</label>
            <textarea class="form-control widget-code" readonly rows="8">${escapeHtml(widget.htmlContent || '')}</textarea>
          </div>
          <div class="form-group">
            <label class="form-label">CSS</label>
            <textarea class="form-control widget-code" readonly rows="8">${escapeHtml(widget.cssContent || '')}</textarea>
          </div>
          <div class="form-group">
            <label class="form-label">JavaScript</label>
            <textarea class="form-control widget-code" readonly rows="8">${escapeHtml(widget.jsContent || '')}</textarea>
          </div>
        </div>
      </div>
    `;
  }

  renderList() {
    if (this.loading) {
      return '<div class="loading-state"><span class="spinner"></span></div>';
    }
    if (this.widgets.length === 0) {
      return '<p class="form-text">No widgets to moderate.</p>';
    }
    return this.widgets.map((w) => {
      const isExpanded = this.reviewWidgetId === w.id;
      const rowError = isExpanded && this.reviewPreviewError ? this.reviewPreviewError : '';
      const detail = isExpanded ? this.widgetDetailCache[w.id] : null;
      const contextKey = `review-${w.id}`;
      const previewConfigForm = isExpanded
        ? renderPreviewConfigForm(
          detail?.configSchema,
          contextKey,
          this.previewConfigValues[contextKey] || {}
        )
        : '';
      return `
      <div class="admin-widget-row card${isExpanded ? ' admin-widget-row-expanded' : ''}">
        <div class="admin-widget-row-header">
          <div class="admin-widget-info">
            <strong>${escapeHtml(w.name)}</strong>
            <span class="widget-status-badge widget-status-${w.systemCatalogStatus || 'none'}">${escapeHtml(w.systemCatalogStatus || 'none')}</span>
            ${w.isPlatform ? '<span class="widget-status-badge widget-status-platform">platform</span>' : ''}
            <div class="form-text">by ${escapeHtml(w.ownerUsername || 'user #' + w.ownerUserId)}</div>
            ${w.description ? `<div class="form-text">${escapeHtml(w.description)}</div>` : ''}
            ${w.rejectionReason ? `<div class="form-text">Reason: ${escapeHtml(w.rejectionReason)}</div>` : ''}
          </div>
          <div class="admin-widget-actions">
            <button type="button" class="btn btn-secondary btn-sm" data-action="edit-widget" data-id="${w.id}">Edit widget</button>
            <button type="button" class="btn btn-secondary btn-sm" data-action="review" data-id="${w.id}" aria-expanded="${isExpanded}">${isExpanded ? 'Hide review' : 'Review'}</button>
            ${(w.systemCatalogStatus || 'none') === 'pending' ? `
              <button type="button" class="btn btn-primary btn-sm" data-action="approve" data-id="${w.id}">Approve</button>
            ` : ''}
            ${w.systemCatalogStatus === 'approved' ? `
              <button type="button" class="btn btn-secondary btn-sm" data-action="disable" data-id="${w.id}">Disable</button>
            ` : ''}
            ${w.systemCatalogStatus === 'disabled' ? `
              <button type="button" class="btn btn-primary btn-sm" data-action="approve" data-id="${w.id}">Re-approve</button>
            ` : ''}
          </div>
        </div>
        ${isExpanded ? `
          <div class="admin-widget-review-panel">
            <div class="widget-preview-block">
              <h4>Preview</h4>
              <p class="form-text">Sandboxed on widget-host — same as on the creator's profile.</p>
              ${previewConfigForm}
              <p class="form-text widget-preview-error" data-preview-error="review-${w.id}" role="alert"${rowError ? '' : ' hidden'}>${escapeHtml(rowError)}</p>
              <iframe
                class="admin-widget-preview-frame widget-preview-frame"
                name="admin-widget-preview-${w.id}"
                data-preview-name="admin-widget-preview-${w.id}"
                data-widget-id="${w.id}"
                title="Preview of ${escapeHtml(w.name)}"
                sandbox="allow-scripts allow-same-origin"
              ></iframe>
            </div>
            ${this.renderOAuthAudit(detail)}
            ${this.renderHostsAudit(detail)}
            ${this.renderCodeSection(detail)}
            ${this.renderModerationSection(w)}
          </div>
        ` : ''}
      </div>
    `;
    }).join('');
  }

  render() {
    this.capturePreviewConfigState();
    this.innerHTML = `
      <div class="admin-widget-management">
        <header class="admin-widget-header">
          <h2>Widget moderation</h2>
          <p class="form-text">Review user-submitted profile widgets before they appear in the public catalog.</p>
        </header>
        <div class="admin-widget-list">${this.renderList()}</div>
      </div>
    `;
  }

  attachListeners() {
    this.querySelectorAll('[data-action="edit-widget"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = parseInt(btn.dataset.id, 10);
        if (Number.isNaN(id)) return;
        window.router?.navigate(`/settings/widgets?edit=${id}`);
      });
    });
    this.querySelectorAll('[data-action="review"]').forEach((btn) => {
      btn.addEventListener('click', () => this.toggleReview(parseInt(btn.dataset.id, 10)));
    });
    this.querySelectorAll('[data-action="approve"]').forEach((btn) => {
      btn.addEventListener('click', () => this.updateStatus(parseInt(btn.dataset.id, 10), 'approved'));
    });
    this.querySelectorAll('[data-action="disable"]').forEach((btn) => {
      btn.addEventListener('click', () => this.updateStatus(parseInt(btn.dataset.id, 10), 'disabled'));
    });
    this.querySelectorAll('[data-action="reject"]').forEach((btn) => {
      btn.addEventListener('click', () => this.rejectWidget(parseInt(btn.dataset.id, 10)));
    });
    this.querySelectorAll('[data-rejection-comment]').forEach((field) => {
      field.addEventListener('input', () => field.classList.remove('is-invalid'));
    });

    this.querySelectorAll('[data-preview-config]').forEach((section) => {
      const contextKey = section.dataset.previewConfig;
      section.querySelectorAll('[data-preview-config-field]').forEach((field) => {
        field.addEventListener('input', () => {
          this.previewConfigValues[contextKey] = readPreviewConfigFromDom(this, contextKey);
          if (contextKey.startsWith('review-')) {
            const widgetId = parseInt(contextKey.slice(7), 10);
            if (!Number.isNaN(widgetId)) {
              if (this._previewDebounceTimer) {
                clearTimeout(this._previewDebounceTimer);
              }
              this._previewDebounceTimer = setTimeout(() => {
                this._previewDebounceTimer = null;
                this.updateReviewPreview(widgetId);
              }, PREVIEW_DEBOUNCE_MS);
            }
          }
        });
      });
    });

    if (this.reviewWidgetId) {
      this.updateReviewPreview(this.reviewWidgetId);
    }
  }
}

customElements.define('admin-widget-management', AdminWidgetManagement);
